import { useCallback, useEffect, useMemo, useState } from "react"

import PageHeader from "@/components/PageHeader"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ToastProvider"
import {
  VenueCandidateRecord,
  CandidateApprovePayload,
  CandidateRejectPayload,
  fetchVenueCandidates,
  uploadVenueDocuments,
  discoverVenueCandidates,
  approveVenueCandidate,
  rejectVenueCandidate,
  pasteVenueText,
  batchApproveVenueCandidates,
} from "@/api/venueIngestion"
import VenueCandidateReview from "@/components/venues/VenueCandidateReview"
import { BandsintownDigestPanel } from "@/components/crm/BandsintownDigestPanel"

const MAX_UPLOAD_FILES = 3

export default function VenueIngestionPage() {
  const { push } = useToast()
  const [tab, setTab] = useState("upload")
  const [candidates, setCandidates] = useState<VenueCandidateRecord[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [pasteText, setPasteText] = useState("")
  const [pasting, setPasting] = useState(false)
  const [batchApproving, setBatchApproving] = useState(false)
  const [batchDismissing, setBatchDismissing] = useState(false)
  const [discovery, setDiscovery] = useState({
    location: "",
    similar_artists: "",
    genres: "",
    limit: 10,
  })

  const loadCandidates = useCallback(async () => {
    setLoadingCandidates(true)
    try {
      const payload = await fetchVenueCandidates({ status: "pending", limit: 100 })
      setCandidates(payload)
    } catch (error) {
      push({ title: "Unable to load candidates", message: "Please try again." })
    } finally {
      setLoadingCandidates(false)
    }
  }, [push])

  useEffect(() => {
    loadCandidates()
  }, [loadCandidates])

  const handleUpload = useCallback(async () => {
    if (!files.length) {
      push({ title: "Attach at least one document", message: "" })
      return
    }
    if (files.length > MAX_UPLOAD_FILES) {
      push({ title: "Maximum 3 files allowed per upload", message: "" })
      return
    }
    setUploading(true)
    try {
      await uploadVenueDocuments(files)
      push({ title: "Documents uploaded", message: "Candidates created for review." })
      setFiles([])
      loadCandidates()
    } catch (error) {
      push({ title: "Upload failed", message: "Please try again." })
    } finally {
      setUploading(false)
    }
  }, [files, loadCandidates, push])

  const handleDiscovery = useCallback(async () => {
    if (!discovery.location.trim()) {
      push({ title: "Location is required", message: "" })
      return
    }
    setSearching(true)
    try {
      await discoverVenueCandidates({
        location: discovery.location,
        similar_artists: discovery.similar_artists
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        genres: discovery.genres
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        limit: discovery.limit,
      })
      push({ title: "Web search complete", message: "New candidates queued for review." })
      loadCandidates()
    } catch (error) {
      push({ title: "Discovery failed", message: "Try adjusting the search and retry." })
    } finally {
      setSearching(false)
    }
  }, [discovery, loadCandidates, push])

  const handleApprove = useCallback(
    async (id: string, payload: CandidateApprovePayload) => {
      try {
        await approveVenueCandidate(id, payload)
        push({ title: "Candidate approved", message: "Venue was added to the library." })
        await loadCandidates()
      } catch (error) {
        push({ title: "Approval failed", message: "Try again after refreshing." })
        throw error
      }
    },
    [loadCandidates, push],
  )

  const handleReject = useCallback(
    async (id: string, reason: string) => {
      try {
        await rejectVenueCandidate(id, { reason })
        push({ title: "Candidate rejected", message: "" })
        await loadCandidates()
      } catch (error) {
        push({ title: "Rejection failed", message: "Try again in a moment." })
        throw error
      }
    },
    [loadCandidates, push],
  )

  const handlePasteText = useCallback(async () => {
    const trimmed = pasteText.trim()
    if (!trimmed) {
      push({ title: "Enter some text", message: "Paste the content before submitting." })
      return
    }
    setPasting(true)
    try {
      await pasteVenueText({ text: trimmed })
      push({ title: "Text parsed", message: "Candidates created from the pasted content." })
      setPasteText("")
      await loadCandidates()
    } catch (error) {
      push({ title: "Extraction failed", message: "Unable to parse the supplied text." })
      throw error
    } finally {
      setPasting(false)
    }
  }, [pasteText, loadCandidates, push])

  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([])

  const fileSummary = useMemo(() => {
    if (!files.length) {
      return "No documents selected"
    }
    return `${files.length} file${files.length > 1 ? "s" : ""} ready`
  }, [files])

  const toggleCandidateSelection = useCallback((id: string, value: boolean) => {
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev)
      if (value) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return Array.from(next)
    })
  }, [])

  const selectAllCandidates = useCallback(() => {
    setSelectedCandidateIds(candidates.map((candidate) => candidate.id))
  }, [candidates])

  const deselectAllCandidates = useCallback(() => {
    setSelectedCandidateIds([])
  }, [])

  const allSelected = candidates.length > 0 && selectedCandidateIds.length === candidates.length

  const handleBatchApprove = useCallback(async () => {
    if (!selectedCandidateIds.length) {
      push({ title: "Select venues to approve", message: "" })
      return
    }
    setBatchApproving(true)
    try {
      const results = await batchApproveVenueCandidates({
        candidates: selectedCandidateIds.map((candidateId) => ({ candidate_id: candidateId })),
      })
      const summary = results.reduce(
        (acc, item) => {
          if (item.success) {
            acc.success += 1
          } else {
            acc.failed += 1
          }
          return acc
        },
        { success: 0, failed: 0 },
      )
      const failureDetails = results
        .filter((item) => !item.success)
        .map((item) => item.detail?.trim())
        .filter((detail): detail is string => Boolean(detail))
      const failureSummary =
        failureDetails.length > 0
          ? (() => {
              const counts = new Map<string, number>()
              for (const detail of failureDetails) {
                counts.set(detail, (counts.get(detail) ?? 0) + 1)
              }
              const topReasons = Array.from(counts.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([detail, count]) => `${detail} (${count})`)
              return topReasons.join(" · ")
            })()
          : ""
      const message = summary.failed
        ? `${summary.success} approved, ${summary.failed} failed${failureSummary ? ` — ${failureSummary}` : ""}`
        : `${summary.success} venues approved`
      push({ title: "Batch approval complete", message })
      setSelectedCandidateIds([])
      await loadCandidates()
    } catch (error) {
      if (error instanceof Error && (error as Error & { code?: string }).code === "timeout") {
        push({
          title: "Batch approval still running",
          message: "This can take a few minutes. Refresh the candidates list to see updates.",
        })
      } else {
        push({ title: "Batch approval failed", message: "Try again after refreshing." })
      }
      throw error
    } finally {
      setBatchApproving(false)
    }
  }, [loadCandidates, push, selectedCandidateIds])

  const handleBatchDismiss = useCallback(async () => {
    if (!selectedCandidateIds.length) {
      push({ title: "Select venues to dismiss", message: "" })
      return
    }
    setBatchDismissing(true)
    const results = { success: 0, failed: 0 }
    try {
      for (const candidateId of selectedCandidateIds) {
        try {
          await rejectVenueCandidate(candidateId, { reason: "Dismissed by reviewer" })
          results.success += 1
        } catch (error) {
          results.failed += 1
        }
      }
      const message = results.failed
        ? `${results.success} dismissed, ${results.failed} failed`
        : `${results.success} venues dismissed`
      push({ title: "Batch dismiss complete", message })
      setSelectedCandidateIds([])
      await loadCandidates()
    } catch (error) {
      push({ title: "Batch dismiss failed", message: "Try again after refreshing." })
      throw error
    } finally {
      setBatchDismissing(false)
    }
  }, [loadCandidates, push, selectedCandidateIds])

  useEffect(() => {
    setSelectedCandidateIds((prev) => prev.filter((id) => candidates.some((candidate) => candidate.id === id)))
  }, [candidates])

  const candidateMarkup = useMemo(() => {
    if (loadingCandidates) {
      return <p className="text-sm text-muted-foreground">Loading candidates…</p>
    }
    if (!candidates.length) {
      return <p className="text-sm text-muted-foreground">No candidate venues pending review.</p>
    }
    return (
      <div className="grid gap-4">
        {candidates.map((candidate) => (
          <VenueCandidateReview
            key={candidate.id}
            candidate={candidate}
            onApprove={handleApprove}
            onReject={handleReject}
            checked={selectedCandidateIds.includes(candidate.id)}
            onToggleSelect={toggleCandidateSelection}
          />
        ))}
      </div>
    )
  }, [
    candidates,
    handleApprove,
    handleReject,
    loadingCandidates,
    selectedCandidateIds,
    toggleCandidateSelection,
  ])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Find Venues"
        description="Upload documents or search the web to discover new venues. All candidates need your approval before being added to your CRM."
      />
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="upload">Upload documents</TabsTrigger>
            <TabsTrigger value="discover">Web search</TabsTrigger>
            <TabsTrigger value="bandsintown">Bandsintown</TabsTrigger>
          </TabsList>
        <TabsContent value="upload" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="venue-upload">Select documents</Label>
              <input
                id="venue-upload"
                type="file"
                multiple
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground/80"
                onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
                accept=".pdf,.doc,.docx,.txt,.csv,.tsv"
              />
              <p className="text-sm text-muted-foreground">{fileSummary}</p>
            </div>
            <div className="flex items-end">
              <Button onClick={handleUpload} disabled={uploading} className="w-full">
                {uploading ? "Uploading…" : "Upload & extract venues"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Uploads are limited to {MAX_UPLOAD_FILES} files and rely on the LLM extraction service.
          </p>
          <div className="space-y-2 pt-2">
            <Label htmlFor="paste-text">Paste document text</Label>
            <Textarea
              id="paste-text"
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
              placeholder="Paste the Bandsintown or venue text here for instant extraction"
              rows={5}
            />
            <div className="flex gap-2">
              <Button onClick={handlePasteText} disabled={pasting || !pasteText.trim()}>
                {pasting ? "Processing…" : "Parse pasted text"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPasteText("")}
                disabled={pasting || !pasteText.trim()}
              >
                Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste raw document text and send it straight to the venue extractor without uploading a file.
            </p>
          </div>
        </TabsContent>
        <TabsContent value="discover" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="discovery-location">Location</Label>
              <Input
                id="discovery-location"
                value={discovery.location}
                onChange={(event) =>
                  setDiscovery((state) => ({ ...state, location: event.target.value }))
                }
                placeholder="City, state or region"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discovery-limit">Max results</Label>
              <Input
                id="discovery-limit"
                type="number"
                min={1}
                max={20}
                value={discovery.limit}
                onChange={(event) =>
                  setDiscovery((state) => ({ ...state, limit: Number(event.target.value) || 10 }))
                }
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="discovery-similar">Similar artists</Label>
              <Textarea
                id="discovery-similar"
                value={discovery.similar_artists}
                onChange={(event) =>
                  setDiscovery((state) => ({ ...state, similar_artists: event.target.value }))
                }
                placeholder="Comma-separated artist names"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discovery-genres">Genres</Label>
              <Textarea
                id="discovery-genres"
                value={discovery.genres}
                onChange={(event) =>
                  setDiscovery((state) => ({ ...state, genres: event.target.value }))
                }
                placeholder="Comma-separated genre labels"
                rows={2}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Searches pull Google Places results and are capped at 20 venues per run.
            </p>
            <Button onClick={handleDiscovery} disabled={searching}>
              {searching ? "Searching…" : "Run web discovery"}
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="bandsintown" className="space-y-4">
          <BandsintownDigestPanel />
        </TabsContent>
      </Tabs>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-semibold text-muted-foreground">Pending candidates</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={selectAllCandidates} disabled={!candidates.length}>
            Select all
          </Button>
          <Button size="sm" variant="ghost" onClick={deselectAllCandidates} disabled={!selectedCandidateIds.length}>
            Deselect all
          </Button>
          <Button
            size="sm"
            onClick={handleBatchApprove}
            disabled={!selectedCandidateIds.length || batchApproving}
          >
            {batchApproving ? "Approving…" : `Approve selected (${selectedCandidateIds.length})`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleBatchDismiss}
            disabled={!selectedCandidateIds.length || batchDismissing}
          >
            {batchDismissing ? "Dismissing…" : `Dismiss selected (${selectedCandidateIds.length})`}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void loadCandidates()}
            disabled={loadingCandidates}
          >
            {loadingCandidates ? "Refreshing…" : "Refresh candidates"}
          </Button>
        </div>
      </div>
      <div>{candidateMarkup}</div>
    </div>
  )
}
